import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  BUTTON_LABELS,
  ENTITY_TYPES,
  MODULE_PERMISSION_MAP,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IPermission, IPermissionCategory } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Typography } from "@mui/material";
import { RoleDepartmentTabView } from "./tabs/RoleDepartmentTabView";
import { RoleDetailsTabView } from "./tabs/RoleDetailsTabView";
import { RoleModulesTabView } from "./tabs/RoleModulesTabView";
import { RolePermissionsSubTabView } from "./tabs/RolePermissionsSubTabView";
import { RolePermissionsTabView } from "./tabs/RolePermissionsTabView";
import { RoleWorkPermissionsSubTabView } from "./tabs/RoleWorkPermissionsSubTabView";
import { hasAnyDependentPermissions } from "./utils/hasAnyDependentPermissions";
import { preFilterModulePermissions } from "./utils/preFilterModulePermissions";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { IRoleFormData } from "../ConfigRoles.types";
import { configRoleSchema } from "../schemas/ConfigRole.schema";
import { useRoleDetailsViewModel } from "./useRoleDetailsViewModel";
import { useRoleWorkPermissionsViewModel } from "./viewModels/useRoleWorkPermissionsViewModel";

const defaultRoleData = {
  name: "",
  description: "",
  permissionNames: [],
  departmentId: "",
  modules: [],
  workPermissionNames: [],
  recordPermissions: [],
};

export const ConfigRoleDetails = ({
  hidePermissions,
  hideWorkPermissions,
}: {
  hidePermissions?: boolean;
  hideWorkPermissions?: boolean;
}) => {
  const [visitedTabs, setVisitedTabs] = useState<string[]>([]);
  const { params, goBack } = useRouter();
  const { id: roleId } = params;

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.ROLE?.UPDATE);

  const form = useForm<IRoleFormData>({
    mode: "onChange",
    defaultValues: defaultRoleData,
    resolver: zodResolver(configRoleSchema),
  });

  const { control, formState, getValues, reset, setValue } = form;
  const { isValid, isDirty } = formState;

  const {
    getRoleDetails,
    handleUpdateRole,
    handleCreateRole,
    permissionsList,
    departments,
    isLoading,
  } = useRoleDetailsViewModel({ setValue });

  const watchWorkPermissions = useWatch({
    control,
    name: "workPermissionNames",
  });

  const {
    orderPermissions,
    workOrderPermissions,
    formatRecordPermissions,
    selectedOrderPermissions,
    selectedWorkOrderPermissions,
  } = useRoleWorkPermissionsViewModel({
    watchWorkPermissions,
    setValue,
  });

  const handleSaveRole = () => {
    const formData = getValues();
    formData.recordPermissions = formatRecordPermissions(formData);

    if (roleId) {
      handleUpdateRole({ ...formData, id: roleId });
    } else {
      handleCreateRole(formData);
    }
  };

  const handleTabChange = (tabValue: string | number | null) => {
    const tab = tabValue?.toString();
    if (tab && !visitedTabs.includes(tab)) {
      setVisitedTabs((prev) => [...prev, tab]);
    }
  };

  const roleActions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            reset();
            goBack();
          },
        },
        submit: {
          label: "Save Role",
          onClick: handleSaveRole,
          disabled: !isValid || !isDirty,
        },
      };

  const watchName = useWatch({
    control,
    name: "name",
  });
  const isNameComplete = Boolean(watchName && watchName.length > 0);

  const watchDescription = useWatch({
    control,
    name: "description",
  });
  const isDescriptionComplete = Boolean(
    watchDescription && watchDescription.length > 0
  );

  const watchPermissions = useWatch({
    control,
    name: "permissionNames",
    compute: (permissions) =>
      permissions.filter(
        (permission) => !permission.toLowerCase().includes("access")
      ) ?? [],
  });

  const isDepartmentSelected = useWatch({
    control,
    name: "departmentId",
    compute: (value) => value !== "",
  });

  const watchedModules = useWatch({
    control,
    name: "modules",
  });

  const hasModules = watchedModules && watchedModules.length > 0;

  const permissionsListWithModules = (() => {
    const prefilteredPermissions = preFilterModulePermissions(
      permissionsList || []
    );

    const moduleAccessPermissionNames: string[] = watchedModules
      .map((module) => MODULE_PERMISSION_MAP[module].access)
      .filter((permissionName) => Boolean(permissionName));

    const trimmedPermissionsList =
      prefilteredPermissions?.filter((permission: IPermission) =>
        hasAnyDependentPermissions(permission, [
          ...watchPermissions,
          ...moduleAccessPermissionNames,
        ])
      ) || [];

    watchedModules.forEach((module) => {
      const administrationPermissionName =
        MODULE_PERMISSION_MAP[module].administration;
      const administrationPermission: IPermission | undefined =
        permissionsList?.find(
          (permission) => permission.name === administrationPermissionName
        );
      if (administrationPermission) {
        trimmedPermissionsList.push(administrationPermission);
      }
    });

    return trimmedPermissionsList;
  })();

  const isCreating = useMemo(() => !roleId, [roleId]);

  const isRoleDetailsTabInvalid = useMemo(() => {
    if (isCreating) {
      if (visitedTabs.length > 0) {
        return !isNameComplete || !isDescriptionComplete;
      }
      return false;
    }

    return !isNameComplete || !isDescriptionComplete;
  }, [isCreating, isNameComplete, isDescriptionComplete, visitedTabs]);

  const permissionTabCategories: IPermissionCategory[] = (() => {
    const categories: Record<string, IPermissionCategory> = {};
    permissionsListWithModules?.forEach((permission) => {
      const category = permission.permissionCategory || "No Category";
      if (!categories[category]) {
        categories[category] = {
          key: category,
          name: permission.permissionCategoryDescription,
          permissions: [],
          selectedPermissions: [],
        };
      }
      categories[category].permissions.push(permission);
      if (watchPermissions?.includes(permission.name)) {
        categories[category].selectedPermissions.push(permission);
      }
    });
    return Object.values(categories).sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  })();

  const renderPageTitle = () => {
    const action = roleId ? "Edit" : "Create";
    const roleLabel = watchName ? `${watchName}` : "New Role";
    return (
      <Typography variant="HD4">
        {action} Role:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {roleLabel}
        </Typography>
      </Typography>
    );
  };

  const permissionSubTabs = useMemo(() => {
    return Object.entries(permissionTabCategories).map(([key, category]) => {
      const label =
        category.selectedPermissions.length > 0
          ? `(${category.selectedPermissions.length}) ${category.name}`
          : category.name;
      return {
        label,
        value: key,
        extraTabProps: {
          permissionsList: category.permissions,
          availablePermissions: category.permissions,
          selectedPermissions: category.selectedPermissions,
          isReadOnly,
          categoryName: category.name,
        },
        controlledFormViewComponent: RolePermissionsSubTabView,
        isComplete: category.selectedPermissions.length > 0,
        groupName: category.name,
      };
    });
  }, [permissionTabCategories, watchPermissions, isReadOnly]);

  const tabs: RoleDetailsTabConfig[] = (() => {
    const tabList: RoleDetailsTabConfig[] = [
      {
        label: "Role Details",
        value: "role details",

        controlledFormViewComponent: RoleDetailsTabView,
        isComplete: isNameComplete && isDescriptionComplete,
        hasErrors: isRoleDetailsTabInvalid,
        extraTabProps: { isReadOnly },
      },
      {
        label: "Department",
        value: "department",
        controlledFormViewComponent: RoleDepartmentTabView,
        extraTabProps: { departmentsList: departments, isReadOnly },
        hasErrors: !isDepartmentSelected && visitedTabs.includes("department"),
        isComplete: isDepartmentSelected,
      },
      {
        label: "Modules",
        value: "modules",
        controlledFormViewComponent: RoleModulesTabView,
        extraTabProps: { isReadOnly },
        isComplete: hasModules,
      },
    ];

    if (!hidePermissions) {
      tabList.push({
        label: "Permissions",
        value: "permissions",
        extraTabProps: {
          permissionTabCategories,
        },
        isComplete: watchPermissions && watchPermissions.length > 0,
        controlledFormViewComponent: RolePermissionsTabView,
        subTabs: permissionSubTabs,
      });
    }

    if (!hideWorkPermissions) {
      tabList.push({
        label: "Work Permissions",
        value: "workPermissions",
        controlledFormViewComponent: undefined,
        isComplete: watchWorkPermissions && watchWorkPermissions.length > 0,
        subTabs: [
          {
            label: "Orders",
            value: ENTITY_TYPES.ORDER,
            controlledFormViewComponent: RoleWorkPermissionsSubTabView,
            extraTabProps: {
              permissionsList: orderPermissions,
              availablePermissions: orderPermissions,
              selectedPermissions: selectedOrderPermissions,
              categoryName: ENTITY_TYPES.ORDER,
              isReadOnly,
            },
            isComplete:
              selectedOrderPermissions && selectedOrderPermissions.length > 0,
          },
          {
            label: "Work Orders",
            value: ENTITY_TYPES.WORK_ORDER,
            controlledFormViewComponent: RoleWorkPermissionsSubTabView,
            extraTabProps: {
              permissionsList: workOrderPermissions,
              availablePermissions: workOrderPermissions,
              selectedPermissions: selectedWorkOrderPermissions,
              categoryName: ENTITY_TYPES.WORK_ORDER,
              isReadOnly,
            },
            isComplete:
              selectedWorkOrderPermissions &&
              selectedWorkOrderPermissions.length > 0,
          },
        ],
      });
    }

    return tabList;
  })();

  useEffect(() => {
    if (roleId) {
      getRoleDetails(roleId);
    }
  }, [roleId]);

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={roleActions}
      loading={isLoading}
    >
      <VerticalTabsLayout
        tabs={tabs}
        form={form}
        onTabChange={(item) => handleTabChange(item?.value)}
        transparentBackground
      />
    </ConfigPageLayout>
  );
};

interface RoleDetailsTabConfig {
  label: string;
  value: string;
  controlledFormViewComponent?: React.ComponentType<any>;
  extraTabProps?: Record<string, any>;
  subTabs?: RoleDetailsTabConfig[];
  isComplete?: boolean;
  hasErrors?: boolean;
}
